import React, {useContext, useEffect, useRef, useState} from "react";
import {makeStyles} from "@material-ui/core";
import {useDrag, useDrop} from "react-dnd";
import {ScreenDesignerContextNew} from "../../../../../../../../ContextComponents/ScreenDesignerContextNew";
import {ContextMenu} from "../RenderByType/ContextMenu";
import {DragHandle} from "../RenderByType/DragHandle"
import {primaryDropColor} from "../../ScreenBar";
import {AcceptTypes, SourceTypes} from "../../../ToolBar/ToolBarUtils/ItemTypes";
import {multiLanguageValue} from "../../../../../../../JsonScreen/Utils/lib";
import {useSelector} from "react-redux";

const useStyles = makeStyles({
    dragger: {
        display: 'none',
        zIndex: 1000
    },
    flexTable: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%'
    },
    flexRow: {
        display: 'flex',
        height: '24px'
    },
    cellHead: {
        border: '1px solid #f2f2f2',
        outline: 'none',
        background: 'rgb(237, 237, 237)',
        position: 'relative',
        '&:hover': {
            '& $dragger': {
                display: 'block'
            }
        }
    },
});

const HorizontalArray = ({children, path, parentPath}) => {
    const classes = useStyles();
    return (
        <div className={classes.flexTable}>
            <div className={classes.flexRow}>
                {children.map((field, index) => {
                    const fieldStyles = field.styles.length ? field.styles[0] : {};
                    return (
                        <HeadItem
                            field={field}
                            children={children}
                            key={index}
                            path={path}
                            parentPath={parentPath}
                            index={index}
                            fieldStyles={fieldStyles}
                        />
                    )
                })}
            </div>
            <div className={classes.flexRow}>
                <TableBody children={children}/>
            </div>
        </div>
    )
};

export default HorizontalArray;

const initialContextState = {
    mouseX: null,
    mouseY: null,
};

const HeadItem = ({field, children, path, parentPath, index, fieldStyles}) => {
    const classes = useStyles();
    const {
        onDropComponent, onDeleteComponent, onSelectComponent,
        onMoveComponent, onDuplicateComponent, onDropMultipleComponents,
        selectedComponent
    } = useContext(ScreenDesignerContextNew);
    const [contextMenu, setContextMenu] = useState(initialContextState);
    const [direction, setDirection] = useState(null);
    const {userLanguage} = useSelector(state => state.app);
    const multiLanguageLabel = multiLanguageValue(field.properties.label, userLanguage)
    const ref = useRef(null);
    const fieldPath = `${path}.children.${index}`;
    const isSelected = selectedComponent && selectedComponent.path === fieldPath;

    function onSelect(e, field, fieldPath) {
        e.stopPropagation();
        onSelectComponent(field, fieldPath)
    }

    function openContextMenu(e, item, path) {
        e.preventDefault();
        e.stopPropagation();
        onSelectComponent(item, path);
        setContextMenu({
            mouseX: e.clientX - 2,
            mouseY: e.clientY - 4,
        })
    }

    function setDir(currentDirection) {
        if (direction !== currentDirection) {
            setDirection(currentDirection)
        }
    }

    const [{isOver, isOverCurrent}, drop] = useDrop({
        accept: AcceptTypes.componentTarget,
        collect: monitor => ({
            isOver: !!monitor.isOver(),
            isOverCurrent: monitor.isOver({shallow: true}),
        }),
        hover: (item, monitor) => {
            if (!ref.current) {
                return
            }
            if (monitor.didDrop()) {
                setDir(null);
                return
            }
            const hoverBoundingRect = ref.current.getBoundingClientRect();
            const clientOffset = monitor.getClientOffset();

            const position = {
                middle: (hoverBoundingRect.right - hoverBoundingRect.left) / 2,
                client: clientOffset.x - hoverBoundingRect.left
            };

            if (position.client < position.middle) {
                setDir('left');
                return
            }
            if (position.client > position.middle) {
                setDir('right')
            }
        },
        drop: (item, monitor) => {
            const pathToDrop = path ? `${path}.children` : 'children';
            if (monitor.didDrop()) {
                return
            }
            const leftRightDirection = direction === 'left' ? index : index + 1;
            if (item.dragType === 'move') {

                //insert panel inside container
                if (item.component.type === 'panel') {
                    return
                }

                onMoveComponent(item.component, item.from, pathToDrop, leftRightDirection, item.index, index);
                return
            }

            if (item.dragType === 'multiple') {
                onDropMultipleComponents(item.component, leftRightDirection, pathToDrop);
                return
            }

            onDropComponent(item, leftRightDirection, pathToDrop)

        }
    });
    const [{isDragging}, drag] = useDrag({
        item: {
            component: {...field, type: SourceTypes.field},
            type: SourceTypes.field,
            dragType: 'move',
            from: children.length > 1 ? `${path}.children.${index}` : parentPath,
            index: index
        },
        canDrag: true,
        collect: monitor => ({
            isDragging: monitor.isDragging(),
        })
    });
    useEffect(() => {
        if (!isOver) {
            setDir(null)
        }
    }, [isOverCurrent, isOver]);
    drop(ref);
    return (
        <>
            <div
                ref={ref}
                onClick={(e) => onSelect(e, field, fieldPath)}
                onContextMenu={(e) => openContextMenu(e, field, fieldPath)}
                className={classes.cellHead}
                style={{
                    opacity: isDragging ? 0.5 : 1,
                    width: fieldStyles.width,
                    border: isSelected ? `1px dashed ${primaryDropColor}` : '1px solid transparent'
                }}
            >
                {multiLanguageLabel}
                <DirectionHighlighter
                    direction={direction}
                    isOver={isOverCurrent}
                    isLast={index === children.length - 1}
                />
                <div ref={drag} className={classes.dragger}>
                    <DragHandle type={'Field'}/>
                </div>
            </div>
            <ContextMenu
                contextMenu={contextMenu}
                onClose={() => setContextMenu(initialContextState)}
                item={field}
                path={fieldPath}
                editItem={onSelectComponent}
                duplicateItem={onDuplicateComponent}
                removeItem={onDeleteComponent}
                parentPath={path}
                index={index}
            />
        </>
    )
};

const TableBody = ({children}) => {
    return children.map((field, index) => {
        const fieldStyles = field.styles.length ? field.styles[0] : {};
        return (
            <div style={{width: fieldStyles.width, border: `1px solid #f2f2f2`}} key={index}>

            </div>
        )
    })
};

const DirectionHighlighter = ({direction, isOver}) => {
    if (!direction && !isOver) return null;

    return (
        <div style={{
            position: 'absolute',
            width: 5,
            top: 0,
            height: '100%',
            background: primaryDropColor,
            zIndex: 1000,
            [direction]: direction === 'left' ? -2 : -5
        }}>

        </div>
    )
};